import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  model,
  OnInit,
  signal,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {
  ButtonComponent,
  ButtonOptions, DateComponent, DateOptions, DefaultSearchComponent,
  FormComponent, InputComponent,
  InputOptions,
  ModalComponent,
  ModalOptions
} from "@shared/components";
import {DropdownComponent, DropdownOptions} from "@shared/components/dropdown/dropdown.component";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  NgForm,
  ReactiveFormsModule, ValidationErrors,
  ValidatorFn,
  Validators
} from "@angular/forms";
import {NgOptimizedImage} from "@angular/common";
import {
  assets,
  FILTER_E_FROM_NUMERIC_INPUT,
  FILTER_NUMBERS_WITH_DECIMALS,
  FILTER_ONLY_NUMBERS,
  FILTER_ONLY_NUMBERS_WITH_DOT
} from '@core/constants';
import {APIService, DataService, ToastService} from '@core/services';
import {PatientService} from '@core/services/patient.service';
import {HTTPMethods, InputModes, InputTypes, ModalTypes} from '@core/enums';
import {ATC} from '@core/classes/other/atc';
import {HttpHeaders} from '@angular/common/http';
import {Product} from '@core/classes/other/product';
import {
  MedicationSearchComponent
} from '@shared/components/dropdown/search/medication-search/medication-search.component';
import {ActiveSubstance} from '@core/classes/other/active-substance';
import {DoseForm} from '@core/classes/other/dose-form';
import {SizeUnit} from '@core/classes/other/size-unit';
import {Route} from '@core/classes/other/route';
import {TranslateService, Translation} from '@ngx-translate/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Response, ResponseSingle} from '@core/classes/other/response';
import {Package} from '@core/classes/other/package';
import {UnitIntake} from '@core/classes/other/unit-intake';
import {Coding} from '@core/classes/other/coding';
import {Medication} from '@core/classes/tables/medication';
import moment from 'moment/moment';
import {checkDate} from '@core/helpers';

@UntilDestroy()
@Component({
  selector: 'ncp-add-medication',
  imports: [
    ButtonComponent,
    DropdownComponent,
    FormsModule,
    ModalComponent,
    NgOptimizedImage,
    ReactiveFormsModule,
    InputComponent,
    DateComponent,
  ],
  templateUrl: './add-medication.component.html',
  styleUrl: './add-medication.component.scss',
})
export class AddMedicationComponent implements OnInit, AfterViewInit {
  @ViewChild('mod') public mod!: ModalComponent;
  @ViewChild('mFrm') protected medicationFormEl?: NgForm;
  @ViewChild('addMedicationButton') public addMedicationButton!: ButtonComponent;

  @ViewChild( 'medicationDesc') public medicationDesc!: DropdownComponent<Product>;
  @ViewChild('quantityIn') public quantityIn!: InputComponent;
  @ViewChild('frequencyIn') public frequencyIn!: InputComponent;
  @ViewChild('durationIn') public durationIn!: InputComponent;
  @ViewChild( 'packageDesc') public packageDesc!: DropdownComponent<Package>;
  @ViewChild( 'unitIntakeDesc') public unitIntakeDesc!: DropdownComponent<UnitIntake>;
  @ViewChild( 'timeUnitDesc') public timeUnitDesc!: DropdownComponent<Coding>;
  @ViewChild( 'durationUnitDesc') public durationUnitDesc!: DropdownComponent<Coding>;
  @ViewChild( 'onSetDate') public onSetDate!: DateComponent;

  @ViewChild('medicationTemplate') public medicationTemplate!: TemplateRef<Product>;

  @ViewChild('addMedicationButton') getSPbtn  = () => {
    return this.addMedicationButton
  }

  private readonly translate = inject(TranslateService);
  protected translations: Translation = this.translate.instant('patient.ps.medication');
  protected readonly toastService = inject(ToastService);
  private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  private apiService = inject(APIService);
  private patientService = inject(PatientService);
  private dataService = inject(DataService);

  protected data = model<any>();

  protected allInfo = model<{
    pbn: Product,
    atc: ATC,
    pas: ActiveSubstance[],
    pdf: DoseForm,
    ppu: SizeUnit,
    prt: Route,
    ppd: Package
    pdu: Coding[]
  } | undefined>()

  private _params = {};
  protected form: FormGroup = new FormGroup({
    coding_id: new FormControl<number | string | undefined>(undefined, [Validators.required]),
    package_id: new FormControl<number | string | undefined>({value: undefined, disabled: true}, [Validators.required]),
    unitsIntake_id: new FormControl<number | string | undefined>({value: undefined, disabled: true}, []),
    frequencyUnits_id: new FormControl<number | string | undefined>({value: undefined, disabled: true}, []),
    durationUnits_id: new FormControl<number | string | undefined>({value: undefined, disabled: true}, []),
    quantity: new FormControl<string>({value: '', disabled: true}, [Validators.min(1)]),
    frequency: new FormControl<string>({value: '', disabled: true}, [Validators.min(1)]),
    duration: new FormControl<string>({value: '', disabled: true}, [Validators.min(1)]),
    onSetDate: new FormControl<string>({value: '', disabled: true}, [Validators.required]),
  }, {
    validators: [this.formValidator()]
  });

  public btnMOptions = signal<ButtonOptions>(
    new ButtonOptions({
      id: 'ndaBtn',
      tooltip: 'add_medication',
    })
  );

  protected modOptions = model<ModalOptions>(
    new ModalOptions({
      id: 'modal',
      title: 'add_medication',
      type: ModalTypes.form,
      form: FormComponent,
      options: {
        animation: true,
        size: 'xl',
        fullscreen: "lg",
        backdrop: 'static',
        keyboard: false,
      },
      onClose: () => {this.onFormReset()}
    })
  );

  protected medicationDescDDOptions = signal<DropdownOptions<Product | ATC>>(
    new DropdownOptions<Product | ATC>({
      id: 'medicationDescription',
      name: 'medicationDescription',
      placeholder: 'medicines_search',
      label: {
        id: "medication_desc_label",
        text: 'medicines_search',
      },
      api: {
        brandName: {
          path: '/get-product',
          headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
          body: {
            description: ''
          },
        },
        activeSubstance: {
          path: '/get-products_substance',
          headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
          body: {
            description: ''
          },
        },
        atcCode: {
          path: '/get-products_atc',
          headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
          body: {
            description: ''
          },
        },
      },
      dataId: 'drugid',
      required: true,
      searchComponent: MedicationSearchComponent,
      formControl: this.form.get('coding_id') as FormControl,
    })
  );

  protected packageDescDDOptions = signal<DropdownOptions<Package>>(
    new DropdownOptions<Package>({
      id: 'packageDescription',
      name: 'packageDescription',
      placeholder: 'package_description',
      label: {
        id: "package_desc_label",
        text: 'package_description',
      },
      dataId: 'packnr',
      required: true,
      searchComponent: null,
      formControl: this.form.get('package_id') as FormControl,
      disabled: true
    })
  );

  protected quantityInputOptions = signal<InputOptions>(
    new InputOptions({
      id: 'quantity',
      name: 'quantity',
      label: {
        id: 'quantity_label',
        text: 'quantity',
      },
      type: InputTypes.number,
      pattern: FILTER_NUMBERS_WITH_DECIMALS,
      formControl: this.form.get('quantity') as FormControl,
      onKeyDown: (event, value) => {
        ["e", "E", "+", "-", /U+0384/].includes(event.key) && event.preventDefault();
      },
      disabled: true,
      min: 1
    })
  )

  protected unitsPerIntakeDDOptions = signal<DropdownOptions<UnitIntake>>(
    new DropdownOptions<UnitIntake>({
      id: 'unitsPerIntake',
      name: 'unitsPerIntake',
      placeholder: 'units_intake_description',
      label: {
        id: "unitsPerIntake_label",
        text: 'units_intake_description',
      },
      searchComponent: null,
      formControl: this.form.get('unitsIntake_id') as FormControl,
      disabled: true
    })
  );

  protected frequencyInputOptions = signal<InputOptions>(
    new InputOptions({
      id: 'frequencyOfUse',
      name: 'frequencyOfUse',
      label: {
        id: 'frequencyOfUse_label',
        text: 'frequency_of_use',
      },
      type: InputTypes.number,
      pattern: FILTER_NUMBERS_WITH_DECIMALS,
      formControl: this.form.get('frequency') as FormControl,
      onKeyDown: (event, value) => {
        ["e", "E", "+", "-", /U+0384/].includes(event.key) && event.preventDefault();
      },
      disabled: true,
      min: 1
    })
  );

  protected frequencyUnitsDDOptions = signal<DropdownOptions<Coding>>(
    new DropdownOptions<Coding>({
      id: 'timeUnit',
      name: 'timeUnit',
      placeholder: 'time_unit_description',
      label: {
        id: "timeUnit_label",
        text: 'time_unit_description',
      },
      searchComponent: null,
      formControl: this.form.get('frequencyUnits_id') as FormControl,
      disabled: true,
    })
  );

  protected durationInputOptions = signal<InputOptions>(
    new InputOptions({
      id: 'treatmentDuration',
      name: 'treatmentDuration',
      label: {
        id: 'treatmentDuration_label',
        text: 'treatment_duration',
      },
      type: InputTypes.number,
      pattern: FILTER_NUMBERS_WITH_DECIMALS,
      formControl: this.form.get('duration') as FormControl,
      onKeyDown: (event, value) => {
        ["e", "E", "+", "-", /U+0384/].includes(event.key) && event.preventDefault();
      },
      disabled: true,
      min: 1
    })
  );

  protected durationUnitsDDOptions = signal<DropdownOptions<Coding>>(
    new DropdownOptions<Coding>({
      id: 'durationUnit',
      name: 'durationUnit',
      placeholder: 'time_unit_description',
      label: {
        id: "durationUnit_label",
        text: 'time_unit_description',
      },
      searchComponent: null,
      formControl: this.form.get('durationUnits_id') as FormControl,
      disabled: true,
    })
  );

  protected onSetDateOptions = signal<DateOptions>(
    new DateOptions({
      id: 'onSetDate',
      label: {
        text: 'on_set',
      },
      name: 'onSetDate',
      initialValue: '',
      formControl: this.form.get('onSetDate') as FormControl,
      required: true,
      minDate: this.patientService.patient?.data?.dob,
      maxDate: moment().format('YYYY-MM-DD'),
    })
  );

  formValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let errors: ValidationErrors | null = {};
      let patientDOB = moment(this.patientService.data?.dob, ['YYYY-MM-DD', 'YYYY/MM/DD', 'YYYY.MM.DD', 'DD-MM-YYYY', 'DD/MM/YYYY', 'DD.MM.YYYY'], true);
      let today = moment();

      let codingId = (control as FormGroup).controls['coding_id'];
      let packageId = (control as FormGroup).controls['package_id'];
      let unitsIntakeId = (control as FormGroup).controls['unitsIntake_id'];
      let frequencyUnitsId = (control as FormGroup).controls['frequencyUnits_id'];
      let durationUnitsId = (control as FormGroup).controls['durationUnits_id'];
      let onSetDate = (control as FormGroup).controls['onSetDate'];

      if(codingId.value === undefined || codingId.value === null) {
        codingId.setErrors({m_codingIdInvalid: true});
        errors['m_codingIdInvalid'] = true;
      }

      if(packageId.enabled && (packageId.value === undefined || packageId.value === null)) {
        packageId.setErrors({m_packageIdInvalid: true});
        errors['m_packageIdInvalid'] = true;
      }

      if(unitsIntakeId.hasValidator(Validators.required) && (unitsIntakeId.value === undefined || unitsIntakeId.value === null)) {
        unitsIntakeId.setErrors({m_unitsIntakeInvalid: true});
        errors['m_unitsIntakeInvalid'] = true;
      }

      if(frequencyUnitsId.hasValidator(Validators.required) && (frequencyUnitsId.value === undefined || frequencyUnitsId.value === null)) {
        frequencyUnitsId.setErrors({m_frequencyUnitsInvalid: true});
        errors['m_frequencyUnitsInvalid'] = true;
      }

      if(durationUnitsId.hasValidator(Validators.required) && (durationUnitsId.value === undefined || durationUnitsId.value === null)) {
        durationUnitsId.setErrors({m_durationUnitsInvalid: true});
        errors['m_durationUnitsInvalid'] = true;
      }

      if(onSetDate.enabled && (!checkDate(onSetDate.value).isValid())) {
        onSetDate.setErrors({m_onSetDateFormatInvalid: true});
        errors['m_onSetDateInvalid'] = true;
      } else if(onSetDate.enabled && (checkDate(onSetDate.value).isValid())) {
        if(checkDate(onSetDate.value).isBefore(patientDOB)) {
          onSetDate.setErrors({m_onSetDateAfterDOB: true});
          errors['m_onSetDateAfterDOB'] = true;
        } else if(checkDate(onSetDate.value).isSameOrAfter(today)) {
          onSetDate.setErrors({m_onSetDateBeforeToday: true});
          errors['m_onSetDateBeforeToday'] = true;
        } else {
          onSetDate.setErrors(null);
        }
      }

      return Object.keys(errors).length > 0 ? errors : null;
    }
  }

  ngOnInit() {
    this.translate.onLangChange.pipe(untilDestroyed(this)).subscribe((event) => {
      this.translations = this.translate.instant('patient.ps.medication');
    });

    this.btnMOptions().onClick = (event) => {
      this.mod.options().onReset = this.onFormReset;

      let medicationDescOptions = this.medicationDescDDOptions();
      medicationDescOptions.itemTemplate = this.medicationTemplate;
      medicationDescOptions.onSelect = this.onSelectMedication;
      medicationDescOptions.onClear = this.onFormReset;
      this.medicationDescDDOptions.set(medicationDescOptions)
      this.medicationDesc.detectChanges();

      let unitIntakeOptions = this.unitsPerIntakeDDOptions();
      unitIntakeOptions.onSelect = this.onSelectIntakeUnit;
      this.unitsPerIntakeDDOptions.set(unitIntakeOptions);
      this.unitIntakeDesc.detectChanges();

      let timeUnitOptions = this.frequencyUnitsDDOptions();
      timeUnitOptions.onSelect = this.onSelectTimeUnit;
      this.frequencyUnitsDDOptions.set(timeUnitOptions);
      this.timeUnitDesc.detectChanges();

      let durationOptions = this.durationUnitsDDOptions();
      durationOptions.onSelect = this.onSelectDurationUnit;
      this.durationUnitsDDOptions.set(durationOptions);
      this.durationUnitDesc.detectChanges();

      this.addMedicationButton.blur();
      this.mod.open();
    };

    this.modOptions().onSave = this.onSave;

    this.form.valueChanges.subscribe(value => {
      if(this.form.disabled) {
        return;
      }

      if(value?.coding_id) {
        if(this.form.get('package_id')?.disabled) {
          this.packageDesc?.enable();
        }
        if(this.form.get('quantity')?.disabled) {
          this.quantityIn?.enable();
        }
        if(this.form.get('frequency')?.disabled) {
          this.frequencyIn?.enable();
        }
        if(this.form.get('duration')?.disabled) {
          this.durationIn?.enable();
        }
      }

      // QUANTITY
      if(
        (value.quantity && Number(value.quantity) > 0) &&
        this.form.get('quantity')?.valid &&
        this.form.get('unitsIntake_id')?.disabled
      ) {
        this.unitIntakeDesc?.enable();
        this.unitsPerIntakeDDOptions().required = true;
        this.form.get('unitsIntake_id')?.setValidators([Validators.required]);
      }

      if(
        ((!value.quantity &&
        this.form.get('quantity')?.valid) ||
        (value.quantity <= 0 &&
        !this.form.get('quantity')?.valid)) &&
        this.form.get('unitsIntake_id')?.enabled
      ) {
        this.unitIntakeDesc?.disable();
        this.unitsPerIntakeDDOptions().required = false;
        this.form.get('unitsIntake_id')?.clearValidators();
      }

      // FREQUENCY
      if(
        (value.frequency && Number(value.frequency) > 0) &&
        this.form.get('frequency')?.valid &&
        this.form.get('frequencyUnits_id')?.disabled
      ) {
        this.timeUnitDesc?.enable();
        this.frequencyUnitsDDOptions().required = true;
        this.form.get('frequencyUnits_id')?.setValidators([Validators.required]);
      }

      if(
        ((!value.frequency &&
        this.form.get('frequency')?.valid) ||
        (value.frequency <= 0 &&
        !this.form.get('frequency')?.valid)) &&
        this.form.get('frequencyUnits_id')?.enabled
      ) {
        this.timeUnitDesc?.disable();
        this.frequencyUnitsDDOptions().required = false;
        this.form.get('frequencyUnits_id')?.clearValidators();
      }

      // DURATION
      if(
        (value.duration  && Number(value.duration) > 0) &&
        this.form.get('duration')?.valid &&
        this.form.get('durationUnits_id')?.disabled
      ) {
        this.durationUnitDesc?.enable();
        this.durationUnitsDDOptions().required = true;
        this.form.get('durationUnits_id')?.setValidators([Validators.required]);
      }

      if(
        ((!value.duration &&
        this.form.get('duration')?.valid) ||
        (value.duration <= 0 &&
        !this.form.get('duration')?.valid)) &&
        this.form.get('durationUnits_id')?.enabled
      ) {
        this.durationUnitDesc?.disable();
        this.durationUnitsDDOptions().required = false;
        this.form.get('durationUnits_id')?.clearValidators();
      }
    });
  }

  onFormReset = (clearMedication: boolean = true) => {
    console.log('on reset form');
    this.form.reset();
    if(clearMedication) {
      this.medicationDesc.reset();
    }
    this.unitIntakeDesc.reset();
    this.timeUnitDesc.reset();
    this.durationUnitDesc.reset();
    this.packageDesc.reset();

    this.quantityIn?.reset();
    this.frequencyIn?.reset();
    this.durationIn?.reset();

    this.onSetDate.reset();

    this.modOptions().errors = [];
  }

  ngAfterViewInit(): void {

  }

  onSelectMedication = (data?: Product) => {
    if(data) {
      this.form.disable();
      this.medicationDescDDOptions().isLoading = true;
      this.apiService.callMany(
        [
          {
            path: '/get-atc_code',
            headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
            body: {
              atc: data.atc
            },
            name: 'atc'
          },
          {
            path: '/get-product_active_substance',
            headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
            body: {
              drugid: data.drugid,
            },
            name: 'pas'
          },
          {
            path: '/get-product_form',
            headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
            body: {
              drugid: data.drugid,
            },
            name: 'pdf'
          },
          {
            path: '/get-product_pack_unit',
            headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
            body: {
              drugid: data.drugid,
            },
            name: 'ppu'
          },
          {
            path: '/get-product_route',
            headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
            body: {
              drugid: data.drugid,
            },
            name: 'prt'
          },
          {
            path: '/get-product_pack_desc',
            headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
            body: {
              drugid: data.drugid,
            },
            name: 'ppd'
          },
          {
            path: '/get-product_duration_unit',
            headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
            body: {

            },
            name: 'pdu'
          }
        ]
      )?.subscribe((res: any) => {
        if(res &&
          res['atc'] &&
          res['pas'] &&
          res['pdf'] &&
          res['ppu'] &&
          res['prt'] &&
          res['ppd'] &&
          res['pdu']) {
          let packageData = (res['ppd'] as Response<Package>).data!
          packageData.map(item => {
            item.description = item.packageDescription;
          })
          let allData = {
            pbn: data,
            atc: (res['atc'] as Response<ATC>).data?.[0]!,
            pas: (res['pas'] as Response<ActiveSubstance>).data!,
            pdf: (res['pdf'] as Response<DoseForm>).data?.[0]!,
            ppu: (res['ppu'] as Response<SizeUnit>).data?.[0]!,
            prt: (res['prt'] as Response<Route>).data?.[0]!,
            pdu: (res['pdu'] as Response<Coding>).data!,
            ppd: packageData,
          };
          this.allInfo.set(allData);

          this.packageDesc.filteredData = packageData;
          this.packageDesc.detectChanges();

          const unitsPerIntakeList: Array<UnitIntake> = [
            {
              description: this.translations.not_applicable,
              coding_id: -1,
              drugid: data.drugid,
            },
            {
              description: allData.ppu.packageSizeUnit,
              coding_id: -1,
              drugid: data.drugid
            },
            {
              description: "dose(s)",
              coding_id: -1,
              drugid: data.drugid,
            },
            {
              description: "drop(s)",
              coding_id: -1,
              drugid: data.drugid,
            },
            {
              description: "gram",
              coding_id: -1,
              drugid: data.drugid,
            },
            {
              description: "international unit",
              coding_id: -1,
              drugid: data.drugid,
            },
            {
              description: "kilo international unit",
              coding_id: -1,
              drugid: data.drugid,
            },
            {
              description: "liters",
              coding_id: -1,
              drugid: data.drugid,
            },
            {
              description: "microequivalent",
              coding_id: -1,
              drugid: data.drugid,
            },
            {
              description: "microgram",
              coding_id: -1,
              drugid: data.drugid,
            },
            {
              description: "milliequivalent",
              coding_id: -1,
              drugid: data.drugid,
            },
            {
              description: "milligram",
              coding_id: -1,
              drugid: data.drugid,
            },
            {
              description: "milliliter",
              coding_id: -1,
              drugid: data.drugid,
            },
            {
              description: "million international unit",
              coding_id: -1,
              drugid: data.drugid,
            }
          ];

          const timeUnits = [
            {
              epSOS_OID: "1.3.6.1.4.1.12559.11.10.1.3.1.42.16",
              coding_id: 22977,
              code: "/d",
              value: 'hour',
              description: this.translations.hour
            },
            {
              epSOS_OID: "1.3.6.1.4.1.12559.11.10.1.3.1.42.16",
              coding_id: 22905,
              code: "/d",
              value: 'day',
              description: this.translations.day
            },
            {
              epSOS_OID: "1.3.6.1.4.1.12559.11.10.1.3.1.42.16",
              coding_id: 23371,
              code: "/h",
              value: 'week',
              description: this.translations.week
            },
            {
              epSOS_OID: "1.3.6.1.4.1.12559.11.10.1.3.1.42.16",
              coding_id: 23161,
              code: "/h",
              value: 'month',
              description: this.translations.month
            }
          ];

          this.allInfo()?.pdu?.map((item: Coding) => {
            item['value'] = item.description;
            item.description = this.translations[item.description!];
          })

          this.unitIntakeDesc.filteredData = unitsPerIntakeList;
          this.unitIntakeDesc.detectChanges();

          this.timeUnitDesc.filteredData = timeUnits;
          this.timeUnitDesc.detectChanges();

          this.durationUnitDesc.filteredData = this.allInfo()?.pdu || [];
          this.durationUnitDesc.detectChanges();

          this.form.enable();
          this.medicationDescDDOptions().isLoading = false;
        }
      })
    } else {
      this.allInfo.set(undefined);
      this.packageDesc.filteredData = [];
      this.packageDesc.detectChanges();
    }
  }

  onSelectIntakeUnit = (data?: UnitIntake) => {

  }

  onSelectTimeUnit = (data?: Coding) => {

  }

  onSelectDurationUnit = (data?: Coding) => {

  }

  onSave = () => {
    this.medicationFormEl?.ngSubmit.emit();
  }

  validate = (): boolean => {
    Object.keys(this.form.controls).forEach((control) => {
      if(this.form.controls[control].enabled) {
        this.form.controls[control].markAsTouched();
      }
    })

    return true;
  }

  onFormSubmit = () => {
    if(this.validate() && this.form.invalid) {
      console.log('this.form.is.invalid');
      this.modOptions().errors = this.form.errors!;
      this.mod.detectChanges();
      return;
    }

    this.modOptions().disableOnSave = true;
    let body = {
      searchName: this.medicationDesc.searchTerms['medication'],
      productName: this.allInfo()?.pbn.productName,
      drugid: this.allInfo()?.pbn.drugid,
      strength: this.allInfo()?.pbn.strength,
      atc: this.allInfo()?.atc.code,
      activeSubstances: this.allInfo()?.pas,
      pharmaceutical_dose_form: this.allInfo()?.pdf.form,
      route_of_administration: this.allInfo()?.prt.route,
      quantity: (this.form.get('quantity') as FormControl).value,
      frequency_of_intake: (this.form.get('frequency') as FormControl).value,
      duration: (this.form.get('duration') as FormControl).value,
      funit: this.frequencyUnitsDDOptions().value ? this.frequencyUnitsDDOptions().value!['value'] : "",
      frequency_of_intake_unit_id: this.frequencyUnitsDDOptions().value?.coding_id,
      units_per_intake: this.unitsPerIntakeDDOptions().value?.description,
      units_per_intake_coding_id: this.unitsPerIntakeDDOptions().value?.coding_id,
      duration_unit: this.durationUnitsDDOptions().value ? this.durationUnitsDDOptions().value!['value'] : "",
      duration_unit_id: this.durationUnitsDDOptions().value?.coding_id,
      date_of_onset: (this.form.get('onSetDate') as FormControl).value,
      packnr: this.packageDescDDOptions().value!.packnr,
      packageDescription: this.packageDescDDOptions().value!.description,
      status: 1,
      version_id: this.patientService.version!.new,
    };

    this.apiService.call({
      path: '/medication',
      headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
      type: HTTPMethods.POST,
      body: body
    })?.subscribe((res: ResponseSingle<Medication>) => {
      if(res && res.status?.toString() === '1') {
        let data = this.data()?.tblOptions().data
        data.unshift(res.data);
        if (this.data && this.data() && this.data().tbl && this.data().tbl.tableData) {
          this.data()!.tbl!.tableData! = data;
        }
        this.mod.close();
      }
    })
  }

  protected readonly assets = assets;
}
